import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useChangeBooleans, useComponentProperties, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { useEffect, useState } from "react";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useTranslation } from "react-i18next";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";
import { isLanguagesChanged } from "../utils";

function Tab() {

    const formProperties = componentPropertiesMap.Tab;

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedComponent.parentId, selectedPage._id);
    const project = usePropsStoreValue("project");

    const [properties, setProperty] = useComponentProperties();
    const [{ isGeneralPropertiesChanged }, setChangeProperty] = useChangeBooleans();

    const { t } = useTranslation("display/common", { useSuspense: false });

    useEffect(() => {
        setProperties();
        setLoading(false);
    }, [])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!loading) {
            setIsChanged();
        }
    }, [properties]);

    // state
    const [tabSelected, setTabSelected] = useState(0);
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    // functions
    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
            setChangeProperty("isGeneralPropertiesChanged", false);
        }
    }

    function handleLabel(event) {
        setProperty("label", event.target.value);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "languages":
                    if (properties.languages && isLanguagesChanged(properties.languages, selectedComponent.languages)) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
            if (isChanged) {
                break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    function setLabelTranslation(data) {
        setProperty("languages", data);
        setShowLabelTranslationDialog(false);
    }

    // handlers
    function handleTabSelect(event) {
        setTabSelected(event.selected);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
    }

    if (loading) {
        return <ComponentFormSkeleton />
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general")}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <div className="form-label" required>
                                    {t("form_labels.label")}
                                </div>
                                <input
                                    placeholder="Enter Label"
                                    className="form-control w-50"
                                    name="Label"
                                    maxLength={256}
                                    onChange={handleLabel}
                                    value={properties.label}
                                    disabled={selectedComponent.hidden}
                                />
                                <div className="col-12">
                                    <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                                        setShowLabelTranslationDialog(true)
                                    }}>
                                        <span className="text-primary">
                                            {t("form_labels.label_translation")}
                                        </span>
                                    </div>
                                </div>
                                {showLabelTranslationDialog && (
                                    <GenericTranslationDialog
                                        onClose={() => { setShowLabelTranslationDialog(false) }}
                                        onSave={setLabelTranslation}
                                        languages={properties.languages}
                                        project={project}
                                        fields={["label"]}
                                    />
                                )}
                            </form>
                        </Scrollable>
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default Tab;